/**
 * Request Access Button Component
 * 
 * Button and modal for users to request free access to private jobs
 * Shows when user doesn't have access and job is not for sale (or as alternative)
 */

import React, { useState, useEffect } from 'react';
import { createAccessRequest, checkPendingRequest } from '../../utils/accessRequestHelpers';
import './RequestAccessButton.css';

const RequestAccessButton = ({ job, user, hasAccess, onRequestSent }) => {
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('investigation');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  // Check for existing pending request
  useEffect(() => {
    if (user && job) {
      checkPendingRequest(user.uid, job.id).then(request => {
        setPendingRequest(request);
      });
    }
  }, [user, job]);

  // Don't show if:
  // - No user
  // - User already has access
  // - User owns the job
  // - Job is public
  if (!user || hasAccess || !job || job.userId === user.uid || job.isPublic) {
    return null;
  }

  // If pending request exists, show different button
  if (pendingRequest) {
    return (
      <div className="request-status-message">
        <span className="status-icon">⏳</span>
        <span>Request Pending - Waiting for owner response</span>
      </div>
    );
  }

  const handleSubmitRequest = async () => {
    // Validate message
    if (!message.trim() || message.trim().length < 10) {
      setError('Please provide a message (at least 10 characters) explaining why you need access');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await createAccessRequest({
        requesterId: user.uid,
        jobId: job.id,
        evidenceId: job.evidenceId,
        casefileId: job.casefileId,
        ownerId: job.userId,
        message: message.trim(),
        reason,
        requesterInfo: {
          email: user.email,
          name: user.displayName || 'Anonymous User'
        },
        jobInfo: {
          title: job.selectedModel || 'AI Analysis',
          casefileName: job.casefileName || 'Private Investigation'
        }
      });

      setSuccess(true);
      
      // Update pending request status
      setPendingRequest({ status: 'pending' });
      
      // Clear form
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
        setMessage('');
        setReason('investigation');
        
        // Notify parent component
        if (onRequestSent) {
          onRequestSent();
        }
      }, 2000);

    } catch (err) {
      console.error('Error requesting access:', err);
      
      // Check if already have pending request
      if (err.message && err.message.includes('pending request')) {
        setError('You already have a pending request for this job. Please wait for the owner to respond.');
        setPendingRequest({ status: 'pending' }); // Update state
      } else {
        setError(err.message || 'Failed to send request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setShowModal(false);
      setError('');
      setMessage('');
    }
  };

  return (
    <>
      <button 
        className="request-access-btn"
        onClick={() => setShowModal(true)}
        title="Request free access from the owner"
      >
        🙋 Request Access
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request Access</h3>
              <button 
                className="close-btn"
                onClick={handleClose}
                disabled={loading}
              >
                ×
              </button>
            </div>
            
            {success ? (
              <div className="success-state">
                <div className="success-icon">✅</div>
                <h4>Request Sent!</h4>
                <p>The owner will be notified and can approve or deny your request.</p>
                <p className="small-text">You'll be able to access the content if approved.</p>
              </div>
            ) : (
              <>
                <div className="modal-body">
                  <p className="intro-text">
                    Send a request to the owner for free access to this analysis and video. 
                    Explain why you need access and the owner will decide.
                  </p>

                  {/* Reason Dropdown */}
                  <div className="form-group">
                    <label htmlFor="reason">Reason for request:</label>
                    <select 
                      id="reason"
                      value={reason} 
                      onChange={(e) => setReason(e.target.value)}
                      disabled={loading}
                    >
                      <option value="investigation">Active Investigation</option>
                      <option value="research">Research / Training</option>
                      <option value="legal">Legal Case</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Message Textarea */}
                  <div className="form-group">
                    <label htmlFor="message">
                      Message to owner: <span className="required">*</span>
                    </label>
                    <textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Explain why you need access to this analysis... (minimum 10 characters)"
                      rows={5}
                      maxLength={500}
                      disabled={loading}
                    />
                    <div className="char-count">
                      {message.length}/500 characters
                      {message.length < 10 && message.length > 0 && (
                        <span className="warning"> (minimum 10 required)</span>
                      )}
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="info-box">
                    <strong>📌 What happens next:</strong>
                    <ul>
                      <li>Owner receives your request</li>
                      <li>They can approve or deny it</li>
                      <li>If approved, you get full access</li>
                      <li>Owner can revoke access anytime</li>
                      <li>Request expires in 30 days if not answered</li>
                    </ul>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="error-message">
                      ❌ {error}
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  <button 
                    onClick={handleClose}
                    disabled={loading}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSubmitRequest}
                    disabled={loading || message.trim().length < 10}
                    className="btn-primary"
                  >
                    {loading ? '📤 Sending...' : '📤 Send Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RequestAccessButton;

